import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import "moment/locale/gu";
import { ArrowLeft, Download, PackageCheck, Building2, Phone, User, Calendar, Pencil } from "lucide-react";
import { toast } from "react-toastify";
import Layout from "../../../components/layout";
import { useCanAccess } from "../../../hooks/useCanAccess";
import { localizedProcessName, formatSizeLabel, formatSize } from "../../../lib/utils";
import { GET } from "../../../services/axios.service";
import { toastStyles } from "../../../static/styles";
import { route } from "../../../static/route";
import { t } from "../../../locales";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../../../components/ui/table";
import { Badge } from "../../../components/ui/badge";

// Aluminium casting lines carry no item at all — they're a raw-material
// intake row instead, so fall back to the raw material (Material + size)
// rather than showing "-" in the Item column.
const itemOrRawMaterial = (item: any): string =>
  item.item?.name || formatSizeLabel(item.size ? { size: item.size.size, material_name: item.size.material?.name } : null);

const ProcessReceiveViewPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canAccess = useCanAccess();
  const [receive, setReceive] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  moment.locale("gu");

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    GET(`/v1/process-receives/${id}`, {})
      .then((res: any) => {
        if (res.data.status === "success") setReceive(res.data.data);
        else toast.error(res.data.message, { ...(toastStyles as any) });
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || t.common.somethingWentWrong, { ...(toastStyles as any) });
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const response: any = await GET(`/v1/process-receives/${id}/pdf`, {});
      if (response.data.status === "success") {
        const link = response.data.data.download_link;
        const w = window.open(link, "_blank");
        if (!w || w.closed) window.location.href = link;
      } else {
        toast.error(response.data.message, { ...(toastStyles as any) });
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t.common.somethingWentWrong, { ...(toastStyles as any) });
    }
    setPdfLoading(false);
  };

  // Deep-linked here (e.g. from the party payment ledger) with no ?process=
  // query param, so — unlike the list/create/edit pages — this page resolves
  // its process definition from the fetched receive itself, not the query string.
  const processDefinition = receive?.process_definition ?? null;
  const totalQty = receive?.items?.reduce((s: number, i: any) => s + Number(i.qty || 0), 0) || 0;
  const listRoute = processDefinition ? `${route.processReceives}?process=${processDefinition.slug}` : route.dashboard;
  const editRoute = processDefinition ? `${route.processReceivesEdit.replace(":id", String(receive?.id))}?process=${processDefinition.slug}` : route.dashboard;
  const isAluminiumCasting = processDefinition?.handler_key === "casting" && receive?.type === "aluminium";
  const showWeightColumn = processDefinition?.rate_basis === "weight" && !isAluminiumCasting;
  const showMoneyColumns = !isAluminiumCasting;

  return (
    <Layout>
      <div className="px-2 py-4 md:px-6 md:py-6">
        <nav className="text-sm font-semibold text-gray-500 mb-4">
          <Link to={route.dashboard} className="text-brand-dark hover:underline">{t.processReceives.breadcrumbHome}</Link>
          <span className="mx-2">&rsaquo;</span>
          {processDefinition && (
            <>
              <Link to={listRoute} className="text-brand-dark hover:underline">
                {t.processReceives.breadcrumbList}
              </Link>
              <span className="mx-2">&rsaquo;</span>
            </>
          )}
          <span className="text-gray-500">{t.processReceives.viewBreadcrumb}</span>
        </nav>

        <h1 className="text-2xl font-bold text-[#113F67] mb-6">
          {processDefinition ? `${localizedProcessName(processDefinition)} — ${t.processReceives.viewTitle}` : t.processReceives.viewTitle}
        </h1>

        {loading ? (
          <Card>
            <CardContent className="py-12">
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                {t.processReceives.loadingItems}
              </div>
            </CardContent>
          </Card>
        ) : !receive ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              {t.processReceives.notFound}
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-lg border-0">
            <CardContent className="p-0">
              <div className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[#113F67]/10 flex items-center justify-center flex-shrink-0">
                      <PackageCheck className="h-5 w-5 text-[#113F67]" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-[#113F67]">{receive.doc_no || t.processReceives.receiveIdLabel}</h2>
                      <p className="text-sm text-gray-500">ID: {receive.id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" className="h-10 px-4 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67] text-sm"
                      onClick={() => navigate(listRoute)}>
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      {t.processReceives.backToList}
                    </Button>
                    <Button className="h-10 px-4 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67] text-sm"
                      onClick={handleDownloadPdf} disabled={pdfLoading}>
                      {pdfLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      {t.processReceives.downloadPdf}
                    </Button>
                    {canAccess("process-receives.update") && receive.can_edit !== false && (
                      <Button
                        variant="outline"
                        className="h-10 px-4 rounded-lg border-[#113F67] text-[#113F67] hover:bg-[#113F67] hover:text-white text-sm"
                        onClick={() => navigate(editRoute)}
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        {t.processReceives.editReceive}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">{t.processReceives.dateLabel}: </span>
                    <span className="font-medium text-gray-700">{moment(receive.date).format("DD MMMM, YYYY hh:mm A")}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">{t.processReceives.totalQty}: </span>
                    <span className="font-medium text-gray-700">{totalQty}</span>
                  </div>
                  {canAccess("money-view") && showMoneyColumns && (
                    <div>
                      <span className="text-gray-500">{t.processReceives.grandTotalLabel}: </span>
                      <span className="font-bold text-[#113F67]">
                        {Number(receive.grand_total).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-200" />

              <div className="p-4 md:p-6">
                <h3 className="text-base font-bold text-[#113F67] mb-3 flex items-center gap-2">
                  {t.processReceives.partyInfoTitle}
                  <Badge className="text-[11px]" variant={receive.mode === "outsource" ? "default" : "secondary"}>
                    {receive.mode === "outsource" ? t.processReceives.outsourceLabel : t.processReceives.inHouseLabel}
                  </Badge>
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="font-semibold text-gray-900">{receive.party?.firm_name || "-"}</span>
                  </div>
                  {receive.party?.name && (
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-600">{receive.party.name}</span>
                    </div>
                  )}
                  {receive.party?.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-600">{receive.party.phone}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-200" />

              <div className="p-4 md:p-6">
                <h3 className="text-base font-bold text-[#113F67] mb-4">{t.processReceives.itemsInfoTitle}</h3>

                <div className="hidden md:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">{t.processReceives.viewTable.sr}</TableHead>
                        <TableHead>
                          {isAluminiumCasting ? t.processReceives.viewTable.rawMaterial : t.processReceives.viewTable.item}
                        </TableHead>
                        {!isAluminiumCasting && <TableHead>{t.processReceives.viewTable.itemSize}</TableHead>}
                        {!isAluminiumCasting && <TableHead>{t.processReceives.viewTable.subProcess}</TableHead>}
                        {showWeightColumn && <TableHead>{t.processReceives.weightLabel}</TableHead>}
                        <TableHead className="text-right">{t.processReceives.viewTable.qty}</TableHead>
                        {canAccess("money-view") && showMoneyColumns && (
                          <>
                            <TableHead className="text-right">{t.processReceives.viewTable.rate}</TableHead>
                            <TableHead className="text-right">{t.processReceives.viewTable.amount}</TableHead>
                          </>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {receive.items?.map((item: any, i: number) => (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium text-gray-500">{i + 1}</TableCell>
                          <TableCell className="font-medium">{itemOrRawMaterial(item)}</TableCell>
                          {!isAluminiumCasting && <TableCell>{formatSize(item.item_size?.size)}</TableCell>}
                          {!isAluminiumCasting && <TableCell>{item.sub_process?.name || "-"}</TableCell>}
                          {showWeightColumn && <TableCell>{item.weight ?? "-"}</TableCell>}
                          <TableCell className="text-right font-medium">{item.qty}</TableCell>
                          {canAccess("money-view") && showMoneyColumns && (
                            <>
                              <TableCell className="text-right">{Number(item.rate).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</TableCell>
                              <TableCell className="text-right font-medium">{Number(item.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                      {canAccess("money-view") && showMoneyColumns && (
                        <TableRow className="bg-gray-50 font-bold">
                          <TableCell colSpan={showWeightColumn ? 5 : 4} className="text-right text-[#113F67]">{t.processReceives.grandTotalLabel}</TableCell>
                          <TableCell colSpan={2} className="text-right text-[#113F67]">
                            {Number(receive.grand_total).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                <div className="md:hidden space-y-3">
                  {receive.items?.map((item: any, i: number) => (
                    <div key={item.id} className="border rounded-lg p-3 bg-gray-50/50">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-xs font-bold text-gray-400">#{i + 1}</span>
                        {canAccess("money-view") && showMoneyColumns && (
                          <span className="font-bold text-[#113F67]">{Number(item.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-500">
                            {isAluminiumCasting ? t.processReceives.viewTable.rawMaterial : t.processReceives.itemLabel}:
                          </span>{" "}
                          <span className="font-medium">{itemOrRawMaterial(item)}</span>
                        </div>
                        {!isAluminiumCasting && (
                          <div><span className="text-gray-500">{t.processReceives.itemSizeLabel}:</span> <span className="font-medium">{formatSize(item.item_size?.size)}</span></div>
                        )}
                        {canAccess("money-view") && showMoneyColumns && (
                          <div><span className="text-gray-500">{t.processReceives.rateLabel}:</span> <span className="font-medium">{Number(item.rate).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></div>
                        )}
                        <div><span className="text-gray-500">{t.processReceives.orderQtyLabel}:</span> <span className="font-medium">{item.qty}</span></div>
                      </div>
                    </div>
                  ))}
                  {canAccess("money-view") && showMoneyColumns && (
                    <div className="bg-gray-50 rounded-xl p-4 text-right">
                      <p className="text-sm text-gray-500">{t.processReceives.grandTotalLabel}</p>
                      <p className="text-xl font-bold text-[#113F67]">
                        {Number(receive.grand_total).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-200" />

              <div className="p-4 md:p-6">
                <h3 className="text-base font-bold text-[#113F67] mb-4">{t.processReceives.accountInfoTitle}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">{t.processReceives.createdAt}</p>
                    <div className="flex items-start gap-2">
                      <Calendar className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-800">{receive.created_by_user?.name || "-"}</p>
                        <p className="text-sm text-gray-500">{moment(receive.created_at).format("DD MMMM, YYYY hh:mm A")}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">{t.processReceives.lastUpdated}</p>
                    <div className="flex items-start gap-2">
                      <Calendar className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500">{moment(receive.updated_at).format("DD MMMM, YYYY hh:mm A")}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default ProcessReceiveViewPage;
